// Client layer (TS-2.5.03) - D-266's Product view: projects grouped by product, read directly
// through the Projects-list data path (not roadmap composition, which stays out of scope for
// v1). Reads for this page gate as roadmap.view (D-267), so every signed-in role sees it - there
// is no role gate in this component, only the route-level RequireRole in App.tsx that matches.
import { useEffect, useState } from "react";
import { ApiError } from "../../api/ApiError";
import type { PagedResult, ProjectSummary } from "../../api/generated/contracts";
import { portfolioDataApi } from "../../api/portfolioData";
import { useProducts } from "../../hooks/useProducts";
import { useInvalidation } from "../../realtime/useInvalidation";
import { EntityTypes } from "../../api/generated/contracts";
import { groupProjectsByProduct } from "./lib/groupProjectsByProduct";
import { ProductGroupCard } from "./ProductGroupCard";
import { strings } from "../../strings";
import "./products.css";

// D-259's documented ceiling (PageDefaults.MaximumSize) - the same "All" value the Projects list's
// own page-size selector sends, requested directly here because this screen has no pager of its
// own to build up to it incrementally: a grouped view needs every project in one read.
const MAX_PAGE_SIZE = 500;

const S = strings.products;

export function ProductsPage() {
  const [page, setPage] = useState<PagedResult<ProjectSummary> | null>(null);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<ApiError | null>(null);

  function refreshProjects() {
    setProjectsLoading(true);
    void portfolioDataApi
      .listProjects({ pageSize: MAX_PAGE_SIZE })
      .then((next) => {
        setPage(next);
        setProjectsError(null);
      })
      .catch((cause: unknown) => {
        setProjectsError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The project list could not be loaded."));
      })
      .finally(() => setProjectsLoading(false));
  }

  useEffect(refreshProjects, []);
  useInvalidation(refreshProjects, EntityTypes.Project);

  // includeRetired: a project that already carries a retired product (D-268) still needs that
  // product's name resolved, not just an orphaned key - the catalogue read must see every product
  // that has ever existed, not only the ones a new assignment may still choose.
  const { products, loading: productsLoading, error: productsError } = useProducts(true);

  const loading = projectsLoading || productsLoading;
  const error = projectsError ?? productsError;

  const groups = page && products ? groupProjectsByProduct(page.items, products) : null;

  return (
    <section className="eg-products">
      <header className="eg-products__header">
        <h1>{S.heading}</h1>
      </header>

      {loading && !groups && (
        <div role="status" aria-live="polite">
          {S.loading}
        </div>
      )}

      {error && !groups && (
        <div role="alert">
          <p>{S.errorTitle}</p>
          <p>{error.message}</p>
        </div>
      )}

      {groups && (
        <div className="eg-products__groups">
          {groups.map((group) => (
            <ProductGroupCard key={group.product?.key ?? "unassigned"} group={group} />
          ))}
        </div>
      )}
    </section>
  );
}
